import { useApolloClient, useMutation } from '@apollo/client';
import type { DeleteAssessmentActivitiesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteAssessmentActivitiesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteAssessmentActivitiesTRPC } from './useDeleteAssessmentActivitiesTRPC';

export const useDeleteAssessmentActivities = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const apolloClient = useApolloClient();

  // GraphQL mutation (legacy)
  const [deleteAssessmentActivitiesGraphQL, graphqlState] = useMutation(
    DeleteAssessmentActivitiesDocument,
    {
      update: (cache) => {
        evictField(cache, 'assessment_activity');
        evictField(cache, 'assessment_activity_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteAssessmentActivitiesTRPC();

  const deleteAssessmentActivities = async (
    ids: string[]
  ): Promise<DeleteAssessmentActivitiesMutation> => {
    if (trpcEnabled) {
      const result = await trpcMutation.deleteAssessmentActivities({
        Ids: ids,
      });
      evictField(apolloClient.cache, 'assessment_activity');
      evictField(apolloClient.cache, 'assessment_activity_aggregate');

      return result;
    }

    const result = await deleteAssessmentActivitiesGraphQL({
      variables: { Ids: ids },
    });

    if (!result.data) {
      throw new Error('Failed to delete assessment activities');
    }

    return result.data;
  };

  return {
    deleteAssessmentActivities,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
